import { forwardRef } from "react";
import { twMerge } from "tailwind-merge";

import { ButtonKind } from "./ButtonKinds";

import { t } from "i18n/i18n";
import { Button, type ButtonProps } from "mds/components/Button";
import { AddPlusIcon } from "mds/icons";
import { useAppSelector } from "store/index";
import { selectIsEditModeEnabled } from "store/selectors";
import { ComponentEventProperties } from "utils/amplitude";

type ChildrenOrReference =
  | {
      children: React.ReactNode;
      reference?: never;
    }
  | {
      children?: never;
      reference:
        | "page"
        | "block"
        | "activity"
        | "topic"
        | "assignment"
        | "course_description"
        | "course_resource"
        | "ingestion"
        | "instructor_workspace"
        | "outcome"
        | "outcome_subgroup"
        | "outcome_group"
        | "outcome_description"
        | "page_group";
    };

type TextAddButtonProps = ComponentEventProperties & {
  onClick?: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
  className?: string;
  kind?: ButtonKind;
  isDisabled?: boolean;
  hideUnlessEditMode?: boolean;
  underlined?: boolean;
  title?: string;
  size?: ButtonProps<"button">["size"];
} & ChildrenOrReference;

export const TextAddButton = forwardRef<HTMLButtonElement, TextAddButtonProps>(
  (
    {
      onClick,
      reference,
      className,
      size = "xs",
      isDisabled,
      title,
      underlined = false,
      hideUnlessEditMode = false,
      kind = "transparentSquareLegacy",
      children,
      ...props
    },
    ref,
  ) => {
    const isEditModeEnabled = useAppSelector(selectIsEditModeEnabled);

    if (hideUnlessEditMode && !isEditModeEnabled) {
      return null;
    }

    const buttonTitle = title || (reference ? t(`common.add.${reference}`) : undefined);

    return (
      <Button
        className={twMerge("flex gap-1", underlined && "ml-1 !text-blue underline", className)}
        disabled={isDisabled}
        kind={kind}
        ref={ref}
        size={size}
        title={buttonTitle}
        onClick={onClick}
        {...props}
      >
        <AddPlusIcon /> {reference ? <span>{buttonTitle}</span> : children}
      </Button>
    );
  },
);
TextAddButton.displayName = "TextAddButton";
